import { collection, query, where, orderBy, limit, getDocs, Timestamp } from 'firebase/firestore';


// Helper function to normalize dates to midnight in local timezone
const normalizeDate = (date) => {
  if (!date) return null;
  const normalized = new Date(date);
  // Set to midnight in local timezone
  return new Date(
    normalized.getFullYear(),
    normalized.getMonth(),
    normalized.getDate(),
    0, 0, 0, 0
  );
};

// Helper function to get recent DORs for a trainee
const getRecentDORs = async (db, traineeId) => {
  try {
    const dorsQuery = query(
      collection(db, 'dors'),
      where('traineeId', '==', traineeId),
      orderBy('date', 'desc'),
      limit(6)
    );

    const dorsSnapshot = await getDocs(dorsQuery);
    const dors = dorsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      date: normalizeDate(doc.data().date.toDate())
    }));

    dors.sort((a, b) => a.date.getTime() - b.date.getTime());
    
   

    return dors;
  } catch (error) {
    console.error('Error fetching recent DORs:', error);
    throw new Error('Failed to fetch recent DORs');
  }
};

// Helper function to get the most recent EOWR
/*
const getMostRecentEOWR = async (db, traineeId, date) => {
  try {
    // Add one full day to include EOWRs from the input date
    const nextDay = new Date(date);
    nextDay.setUTCDate(nextDay.getUTCDate() + 1);
    
   

    const eowrQuery = query(
      collection(db, 'endOfWeekReports'),
      where('traineeId', '==', traineeId),
      where('weekEndingDate', '<', Timestamp.fromDate(nextDay)),
      orderBy('weekEndingDate', 'desc'),
      limit(1)
    );

    const eowrSnapshot = await getDocs(eowrQuery);
    
    if (eowrSnapshot.empty) {
      return null;
    }
    
    const eowrDoc = eowrSnapshot.docs[0];
    const eowrData = {
      id: eowrDoc.id,
      ...eowrDoc.data(),
      weekEndingDate: normalizeDate(eowrDoc.data().weekEndingDate.toDate())
    };

   

    return eowrData;
  } catch (error) {
    console.error('Error getting most recent EOWR:', error);
    throw new Error('Failed to get most recent EOWR');
  }
};
*/





// Updated checkDORConstraints function
const checkDORConstraints = async (db, traineeId, newDORDate) => {
  try {
    const normalizedNewDORDate = normalizeDate(newDORDate);

    // Find the last EOWR date
    const lastEOWRQuery = query(
      collection(db, 'endOfWeekReports'),
      where('traineeId', '==', traineeId),
      where('weekEndingDate', '<=', Timestamp.fromDate(normalizedNewDORDate)),
      orderBy('weekEndingDate', 'desc'),
      limit(1)
    );

    const lastEOWRSnapshot = await getDocs(lastEOWRQuery);

    let lastEOWRDate;
    if (lastEOWRSnapshot.empty) {
      lastEOWRDate = null;
    } else {
      lastEOWRDate = normalizeDate(
        lastEOWRSnapshot.docs[0].data().weekEndingDate.toDate()
      );
    }

    // Set the start date for DOR counting
    let startDate;
    if (lastEOWRDate) {
      startDate = new Date(lastEOWRDate.getTime() + 24 * 60 * 60 * 1000); // Next day after last EOWR
    } else {
      startDate = new Date(0); // Start from epoch if no EOWR exists
    }

    // Query all DORs since last EOWR up to the new DOR date (inclusive)
    const dorsQuery = query(
      collection(db, 'dors'),
      where('traineeId', '==', traineeId),
      where('dateOnly', '>=', Timestamp.fromDate(startDate)),
      where('dateOnly', '<=', Timestamp.fromDate(normalizedNewDORDate)),
      orderBy('dateOnly', 'asc')
    );

    const dorsSnapshot = await getDocs(dorsQuery);
    const dors = dorsSnapshot.docs.map((doc) => ({
      id: doc.id,
      date: normalizeDate(doc.data().dateOnly.toDate()),
    }));

    // Enforce the limit of 5 DORs
    if (dors.length >= 5) {
      return {
        allowed: false,
        message:
          'You have reached the maximum limit of 5 consecutive Daily Observation Reports. You must create an End of Week Report before continuing with new DORs.',
        requiresEOWR: true,
        recentDORs: dors.slice(0, 5),
      };
    }

    return {
      allowed: true,
      recentDORs: dors,
    };
  } catch (error) {
    console.error('Error checking DOR constraints:', error);
    throw new Error('Failed to check DOR constraints');
  }
};









// Updated validateEOWRCreation function
const validateEOWRCreation = async (db, traineeId, eowrDate) => {
  try {
    const normalizedEOWRDate = normalizeDate(eowrDate);

    // Find the last EOWR date
    const lastEOWRQuery = query(
      collection(db, 'endOfWeekReports'),
      where('traineeId', '==', traineeId),
      where('weekEndingDate', '<', Timestamp.fromDate(normalizedEOWRDate)),
      orderBy('weekEndingDate', 'desc'),
      limit(1)
    );

    const lastEOWRSnapshot = await getDocs(lastEOWRQuery);

    let lastEOWRDate;
    if (lastEOWRSnapshot.empty) {
      lastEOWRDate = null;
    } else {
      lastEOWRDate = normalizeDate(
        lastEOWRSnapshot.docs[0].data().weekEndingDate.toDate()
      );
    }

    // Set the start date for DOR retrieval
    let startDate;
    if (lastEOWRDate) {
      startDate = new Date(lastEOWRDate.getTime() + 24 * 60 * 60 * 1000); // Next day after last EOWR
    } else {
      startDate = new Date(0); // Start from epoch if no EOWR exists
    }

    // Query all DORs since last EOWR up to the EOWR date (inclusive)
    const dorsQuery = query(
      collection(db, 'dors'),
      where('traineeId', '==', traineeId),
      where('dateOnly', '>=', Timestamp.fromDate(startDate)),
      where('dateOnly', '<=', Timestamp.fromDate(normalizedEOWRDate)),
      orderBy('dateOnly', 'asc')
    );

    const dorsSnapshot = await getDocs(dorsQuery);
    const dors = dorsSnapshot.docs.map((doc) => ({
      id: doc.id,
      date: normalizeDate(doc.data().dateOnly.toDate()),
    }));

    // Ensure there are DORs to include in the EOWR
    if (dors.length === 0) {
      return {
        allowed: false,
        message:
          'No Daily Observation Reports found after the last End of Week Report.',
      };
    }

    // Ensure the EOWR date is after all included DORs
    const latestDORDate = dors[dors.length - 1].date;
    if (normalizedEOWRDate.getTime() < latestDORDate.getTime()) {
      return {
        allowed: false,
        message:
          'End of Week Report date must be after all included Daily Observation Reports.',
      };
    }

    return {
      allowed: true,
      recentDORs: dors.slice(0, 5),
    };
  } catch (error) {
    console.error('Error validating EOWR creation:', error);
    throw new Error('Failed to validate EOWR creation');
  }
};










export {
  checkDORConstraints,
  validateEOWRCreation,
  getRecentDORs
};